import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { toDatabaseValue } from "@/utils/supabase/type-guards";
import { useCreateNotification } from "@/hooks/use-create-notification";

export const usePromptLike = (promptId: string, user: any, initialLiked = false, initialCount = 0) => {
  const [isLiked, setIsLiked] = useState(initialLiked);
  const [likeCount, setLikeCount] = useState(initialCount);
  const { notifyOnLike } = useCreateNotification();

  useEffect(() => {
    if (!user) return;
    
    const checkLikeStatus = async () => {
      try {
        const { data: likeData, error: likeError } = await supabase
          .from('prompt_likes')
          .select('id')
          .eq('prompt_id', toDatabaseValue(promptId))
          .eq('user_id', toDatabaseValue(user.id))
          .maybeSingle();
          
        if (!likeError) {
          setIsLiked(!!likeData);
        }
        
        const { count, error: countError } = await supabase
          .from('prompt_likes')
          .select('*', { count: "exact", head: true })
          .eq('prompt_id', toDatabaseValue(promptId));
          
        if (!countError && count !== null) {
          setLikeCount(count);
        }
      } catch (error) {
        console.error('Error checking like status:', error);
      }
    };
    
    checkLikeStatus();
  }, [promptId, user]);

  const handleLike = async (newLikedState: boolean) => {
    if (!user) {
      toast.error("Please sign in to like prompts");
      return;
    }
    
    try {
      if (newLikedState) {
        const { error } = await supabase
          .from('prompt_likes')
          .upsert(
            toDatabaseValue({ user_id: user.id, prompt_id: promptId }),
            { onConflict: 'user_id,prompt_id', ignoreDuplicates: true }
          );
          
        if (error) throw error;
        
        setLikeCount(prev => prev + 1);
        setIsLiked(true);

        // Get username using public_profiles view for security
        const { data: profile } = await supabase
          .from('public_profiles')
          .select('username')
          .eq('id', user.id)
          .single();
        
        if (profile?.username) {
          notifyOnLike(promptId, user.id, profile.username);
        }
      } else {
        const { error } = await supabase
          .from('prompt_likes')
          .delete()
          .eq('user_id', toDatabaseValue(user.id))
          .eq('prompt_id', toDatabaseValue(promptId));
          
        if (error) throw error;
        
        setLikeCount(prev => Math.max(0, prev - 1));
        setIsLiked(false);
      }
    } catch (error: any) {
      console.error('Error toggling like:', error);
      toast.error(error.message || "Failed to update like status");
    }
  };

  return {
    isLiked,
    likeCount,
    handleLike,
    setIsLiked,
    setLikeCount
  };
};
